import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';

import { TextField, Dropdown } from '@bingads-webui-react/fabric-control';
import { isValidUrl, getDomain } from '@bingads-webui/url-util';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import {
  placeholderImage,
  getPlaceholderStyles,
  placeholderTextFieldStyles,
  placeholderTextFieldIconProps,
  placeholderDropdownStyles,
} from './styles/index';

const urlPrefixOptions = [
  { key: 'https://', text: 'https://' },
  { key: 'http://', text: 'http://' },
];

const defaultProtocol = 'https://';

export const PlaceholderView = ({
  onEnteredFinalUrl,
  protocol,
  onUpdateProtocol,
  initialFinalUrl,
}) => {
  const [managedProtocol, setManagedProtocol] = useState(defaultProtocol);
  const [isInvalidUrl, setIsInvalidUrl] = useState(false);
  const [initialValue] = useState(initialFinalUrl);

  const initialTextFieldValue = useMemo(() => {
    if (initialValue) {
      return getDomain(initialValue);
    }
    return null;
  }, [initialValue]);

  useEffect(() => {
    if (initialValue && !isValidUrl(initialValue)) {
      setIsInvalidUrl(true);
    }
  }, [initialValue, setIsInvalidUrl]);

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getPlaceholderStyles);

  const shouldUseExternallyManagedProtocol = useMemo(
    () => protocol && onUpdateProtocol
    , [protocol, onUpdateProtocol]
  );

  const currentProtocol = useMemo(() => protocol || managedProtocol, [
    protocol,
    managedProtocol,
  ]);

  const setCurrentProtocol = useCallback((newProtocol, ev) => {
    if (shouldUseExternallyManagedProtocol) {
      onUpdateProtocol(newProtocol, ev);
    } else {
      setManagedProtocol(newProtocol);
    }
  }, [shouldUseExternallyManagedProtocol, onUpdateProtocol, setManagedProtocol]);

  const onChangeProtocol = useCallback((ev, newPrefix) => {
    setCurrentProtocol(newPrefix.text, ev);
  }, [setCurrentProtocol]);

  const onBlurUrl = useCallback((ev) => {
    const newUrl = ev && ev.target && ev.target.value;
    const newUrlWithProtocol = `${currentProtocol}${newUrl}`;

    if (isValidUrl(newUrlWithProtocol)) {
      setIsInvalidUrl(false);
      onEnteredFinalUrl(newUrlWithProtocol, ev);
    } else {
      setIsInvalidUrl(true);
    }
  }, [setIsInvalidUrl, onEnteredFinalUrl, currentProtocol]);

  const onKeyDown = useCallback((ev) => {
    if (ev.key === 'Enter') {
      onBlurUrl(ev);
    }
  }, [onBlurUrl]);

  return (
    <div className={classnames('placeholder-container', classes.placeholderView)}>
      <img src={placeholderImage} alt="" />
      <div className="default-message"> {i18n.getString(_TL_('Please enter your website’s URL. We’ll pull images from your website.'))} </div>
      <Dropdown
        options={urlPrefixOptions}
        selectedKey={currentProtocol}
        onChange={onChangeProtocol}
        name="TextAdUrlPrefixDropdown"
        ariaLabel={i18n.getString(_TL_('Final URL options'))}
        styles={placeholderDropdownStyles}
      />
      <TextField
        className="final-url"
        onBlur={onBlurUrl}
        placeholder={i18n.getString(_TL_('Enter website URL'))}
        styles={placeholderTextFieldStyles}
        iconProps={placeholderTextFieldIconProps}
        onKeyDown={onKeyDown}
        defaultValue={initialTextFieldValue}
      />
      {isInvalidUrl &&
        <div className="suggestion-error-invalid">
          {i18n.getString(_TL_('Please enter a valid Final URL to see images from your website.'))}
        </div>
      }
    </div>
  );
};

PlaceholderView.propTypes = {
  onEnteredFinalUrl: PropTypes.func.isRequired,
  protocol: PropTypes.string,
  onUpdateProtocol: PropTypes.func,
  initialFinalUrl: PropTypes.string,
};

PlaceholderView.defaultProps = {
  protocol: null,
  initialFinalUrl: null,
  onUpdateProtocol: _.noop,
};
